import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Grid } from '@/components/core/Grid';
import { IconButton } from '@/components/core/IconButton';
import { useDynamicPageSize } from '@/hooks/useDynamicPageSize';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useTranslation } from '@/hooks/useTranslation';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { useCommunicationChannelData } from '../communicationChannel/communicationChannel.queries';
import { CommunicationChannelDataType } from '../communicationChannel/communicationChannel.types';
import { useAllContactData } from '../contact/contact.queries';
import { usePublicationColumns } from './columns';
import { publicationListWithEvent, PublicationQueryPage } from './executed.publication';
import {
    useJobData,
    usePublicationData,
} from './executed.publication.queries';
import {
    JobDataType,
    PublicationDataType,
    PublicationEventDataType,
} from './executed.publication.types';

type DestinationLike = {
    id?: number;
    name?: string;
    organizationId?: number;
    subDestinations?: CommunicationChannelDataType['subDestinations'];
};

const buildDestinationNameLookup = (
    contacts: readonly DestinationLike[] | undefined,
): Map<number, string> => {
    const lookup = new Map<number, string>();
    if (!contacts) {
        return lookup;
    }
    for (const dest of contacts) {
        const name = dest?.name ?? '';
        if (typeof dest?.id === 'number' && !lookup.has(dest.id)) {
            lookup.set(dest.id, name);
        }
        if (typeof dest?.organizationId === 'number' && !lookup.has(dest.organizationId)) {
            lookup.set(dest.organizationId, name);
        }
    }
    return lookup;
};

const buildChannelLookup = (
    channels: readonly DestinationLike[] | undefined,
): Map<number, DestinationLike> => {
    const lookup = new Map<number, DestinationLike>();
    if (!channels) {
        return lookup;
    }
    for (const channel of channels) {
        if (typeof channel?.id === 'number' && !lookup.has(channel.id)) {
            lookup.set(channel.id, channel);
        }
    }
    return lookup;
};

const buildJobNameLookup = (
    jobs: readonly JobDataType[] | undefined,
): Map<number, string> => {
    const lookup = new Map<number, string>();
    if (!jobs) {
        return lookup;
    }
    for (const job of jobs) {
        if (typeof job?.id === 'number' && !lookup.has(job.id)) {
            lookup.set(job.id, job.name ?? '');
        }
    }
    return lookup;
};

const enrichPublicationRow = (
    row: PublicationDataType,
    jobNameById: Map<number, string>,
    channelById: Map<number, DestinationLike>,
    destinationNameById: Map<number, string>,
): PublicationDataType => {
    const jobName = jobNameById.get(row.jobId) ?? '';
    let destName: string;

    if (row.subDestination) {
        const destChannel = channelById.get(row.destinationId);
        const pubDescription =
            destChannel?.subDestinations?.[row.subDestination] ?? '';
        destName = `${pubDescription} ${row.subDestination}`;
    } else {
        destName = destinationNameById.get(row.destinationId) ?? '';
    }

    return {
        ...row,
        destinationName: destName,
        jobName,
    };
};

export default function ExecutedPublication(
    {fetchedContacts}: {
        fetchedContacts?: any[]
    } = {},
) {
    const { t } = useTranslation();
    const pageSize = useDynamicPageSize();
    const {
        data: publicationRows,
        isLoading: isPublicationsLoading,
        isFetching: isPublicationsFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch: refetchPublications,
    } = usePublicationData({
        variables: { limit: pageSize },
        select: (data) => data.pages.flatMap((page) => page.data),
        enabled: pageSize > 0,
    });

    const {data: jobs, refetch: refetchJobs} = useJobData({
        variables: { limit: pageSize, type: 'PUBLICATION' },
        select: (data) => data.pages.flatMap((page) => page.data),
        enabled: pageSize > 0,
    });

    const {data: channels, isLoading: isChannelsLoading, isSuccess: isChannelsSuccess}  = useCommunicationChannelData(!fetchedContacts);
    const {data: contacts, isLoading: isContactLoading, isSuccess: isContactSuccess} = useAllContactData(!fetchedContacts);

    const queryClient = useQueryClient();
    const pendingPublicationEventsRef = useRef<PublicationEventDataType[]>([]);

    const onPublicationTaskChanged = useCallback(
        (eventData: PublicationEventDataType) => {
            // LAX-10547: do not cancel the in-flight list query. On a busy
            // organization publicationTaskChanged fires constantly, and
            // cancelQueries aborts the next page so scrolling never advances.
            // The fetch resolves from the pages it captured at start, which
            // drops events that landed mid-request; those are replayed once
            // the fetch settles.
            if (queryClient.isFetching({ queryKey: ['items/publications'] }) > 0) {
                pendingPublicationEventsRef.current.push(eventData);
            }
            queryClient.setQueriesData(
                { queryKey: ['items/publications'] },
                (state: InfiniteData<PublicationQueryPage> | undefined) =>
                    publicationListWithEvent(state, eventData),
            );
        },
        [queryClient],
    );

    const { subscribe: subscribePublicationTasks, unsubscribe: unsubscribePublicationTasks } =
        useRealTimeMessaging({
            eventName: RealTimeMessagingEvents.PUBLICATION_TASK_CHANGED_EVENT,
            eventHandler: onPublicationTaskChanged,
        });

    // Not in application-controller registerMultiple (commented out). Without
    // this subscribe, Deepstream never delivers publicationTaskChanged.
    useEffect(() => {
        void subscribePublicationTasks();
        return () => {
            void unsubscribePublicationTasks();
        };
    }, [subscribePublicationTasks, unsubscribePublicationTasks]);

    useEffect(() => {
        if (isPublicationsFetching || pendingPublicationEventsRef.current.length === 0) {
            return;
        }

        const events = pendingPublicationEventsRef.current.splice(0);
        for (const eventData of events) {
            queryClient.setQueriesData(
                { queryKey: ['items/publications'] },
                (state: InfiniteData<PublicationQueryPage> | undefined) =>
                    publicationListWithEvent(state, eventData),
            );
        }
    }, [isPublicationsFetching, queryClient]);

    const destinationNameById = useMemo<Map<number, string>>(() => {
        if (fetchedContacts) {
            return buildDestinationNameLookup(fetchedContacts as DestinationLike[]);
        }
        if (channels && contacts) {
            return buildDestinationNameLookup([
                ...(channels as unknown as DestinationLike[]),
                ...(contacts as unknown as DestinationLike[]),
            ]);
        }
        return new Map<number, string>();
    }, [fetchedContacts, channels, contacts]);

    const channelById = useMemo<Map<number, DestinationLike>>(
        () => buildChannelLookup(
            (channels as unknown as DestinationLike[] | undefined)
                ?? (fetchedContacts as DestinationLike[] | undefined),
        ),
        [channels, fetchedContacts],
    );

    const jobNameById = useMemo<Map<number, string>>(
        () => buildJobNameLookup(jobs),
        [jobs],
    );

    const rawPublicationRows = publicationRows ?? [];
    const canEnrichRows =
        Boolean(fetchedContacts) ||
        (isContactSuccess && isChannelsSuccess && !isContactLoading && !isChannelsLoading);
    const publicationsData = canEnrichRows
        ? rawPublicationRows.map((pub) =>
            enrichPublicationRow(pub, jobNameById, channelById, destinationNameById),
        )
        : rawPublicationRows;
    const isLoading = isPublicationsLoading && rawPublicationRows.length === 0;

    const refreshExcutedData = async() => {
        await refetchPublications();
        await refetchJobs();
    };

    const publicationGridColumns = usePublicationColumns(refreshExcutedData);
    const executedRows = publicationsData || [];
    const executedGridHeight = executedRows.length === 0
        ? 66
        : 400;
    const loadNextPublications = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return (
        <div className="tw-flex tw-flex-col">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <p className="tw-font-bold tw-m-0">
                    { t('PUBLICATIONS.EXECUTED') }
                </p>
                <IconButton icon={ArrowPathIcon} size='md' onClick={refreshExcutedData} />
            </div>
            <Grid
                className="tw-w-full tw-table-auto tw-break-words"
                style={{ height: executedGridHeight, maxHeight: 400 }}
                columns={publicationGridColumns}
                isWrapContent={true}
                burgerMenu={true}
                isLoading={isLoading}
                storedStateKey={'ExecutedPublication'}
                isLoadingNextPage={isFetchingNextPage}
                data={executedRows}
                onInfiniteScroll={loadNextPublications}
            />
        </div>
    );
}
